import { useMutation, useQuery } from '@apollo/client'
import { ErrorComponent, ProgressSpinner } from '@medical/components'
import ClinicNameRender from '@medical/components/ClinicNameRender'
import {
  STAFF_CHECK_SHIFT,
  STAFF_UPLOAD_SHIFT,
} from '@medical/constant/permissions'
import {
  checkStaffPermission,
  clinicalDepartmentNames,
  convertUserCsvRecord,
} from '@medical/libs'
import Auth from '@medical/middleware/auth'
import { DEPARTMENTS_LIST } from '@medical/pages/staff/CreateAvailableShift/CreateAvailableShift.graphql'
import { useCustom } from '@medical/provider/context'
import downloadCsv from 'download-csv'
import queryString from 'query-string'
import React, { useRef, useState } from 'react'

import { CHECK_UPLOAD_SHIFTS, UPLOAD_SHIFTS } from './WorkScheduleCsv.graphql'
import WorkScheduleCsvScene from './WorkScheduleCsvScene'
import { getUnpublishedShiftSummary } from './unpublishedShiftUpload'

const WorkScheduleCsv = ({ location: { search } }) => {
  const { isAvailableShift } = queryString.parse(search)
  const [{ i18n, popup }] = useCustom()
  const { loading, error, data } = useQuery(DEPARTMENTS_LIST)
  const [uploadShifts] = useMutation(UPLOAD_SHIFTS)
  const [checkUploadShifts] = useMutation(CHECK_UPLOAD_SHIFTS)
  const [state, setState] = useState({
    isUploaded: false,
  })
  const [pendingUpload, setPendingUpload] = useState(null)
  const uploadInFlight = useRef(false)
  const isUploadPermitted = checkStaffPermission({
    functionName: STAFF_UPLOAD_SHIFT,
  })
  const isUploadCheckPermitted = checkStaffPermission({
    functionName: STAFF_CHECK_SHIFT,
  })
  if (loading) return <ProgressSpinner />
  if (error) return <ErrorComponent error={error} />

  const executeUpload = async ({ UserCsvRecords, resetForm }) => {
    if (uploadInFlight.current) return
    uploadInFlight.current = true
    setState({ isUploaded: true })
    try {
      popup.info(i18n.t('staff.uploadCsv.uploading'))
      await uploadShifts({
        variables: {
          UserCsvRecords,
          isAvailableShift: !!isAvailableShift,
        },
      })
      popup.clear()
      popup.success(i18n.t('staff.uploadCsv.submissionSuccess'))
      resetForm()
    } catch (error) {
      popup.clear()
      popup.error(error)
    } finally {
      uploadInFlight.current = false
      setState({ isUploaded: false })
    }
  }

  const onSubmit = async ({ data: UserCsvData }, { resetForm }) => {
    if (!isUploadPermitted) {
      popup.error(i18n.t('main.noPermission'))
      return
    }

    const UserCsvRecords = convertUserCsvRecord(UserCsvData)
    const unpublishedSummary = isAvailableShift
      ? null
      : getUnpublishedShiftSummary(UserCsvRecords)

    if (unpublishedSummary) {
      setPendingUpload({ UserCsvRecords, resetForm, unpublishedSummary })
      return
    }

    await executeUpload({ UserCsvRecords, resetForm })
  }

  const cancelPendingUpload = () => {
    if (!uploadInFlight.current) setPendingUpload(null)
  }

  const confirmPendingUpload = async () => {
    if (!pendingUpload || uploadInFlight.current) return
    const upload = pendingUpload
    setPendingUpload(null)
    await executeUpload(upload)
  }

  const checkCsvData = async ({ data: UserCsvData }, { setFieldValue }) => {
    try {
      if (!isUploadCheckPermitted) {
        popup.error(i18n.t('main.noPermission'))
      } else {
        popup.info(i18n.t('staff.uploadCheckCsv.uploading'))
        const UserCsvRecords = convertUserCsvRecord(UserCsvData)
        const {
          data: { staffCheckUploadShifts },
        } = await checkUploadShifts({
          variables: {
            UserCsvRecords,
            isAvailableShift: !!isAvailableShift,
          },
        })
        // Only recruitment shifts resolve a delete plan, and the column is
        // added only once a row actually deletes something, so an upload with
        // no delete row keeps the table it had before.
        const hasDeletePlan =
          !!isAvailableShift &&
          staffCheckUploadShifts.some(record => record.deleteCount != null)
        const messageAddedCsvData = UserCsvData.map((item, index) => ({
          ...item,
          errorMessage: staffCheckUploadShifts[index].errorMessage,
          ...(hasDeletePlan && {
            deleteCount: staffCheckUploadShifts[index].deleteCount,
          }),
          checked: true,
        }))
        setFieldValue('data', messageAddedCsvData)
        popup.clear()
        popup.success(i18n.t('staff.uploadCheckCsv.submissionSuccess'))
      }
    } catch (error) {
      popup.clear()
      popup.error(error)
    }
  }
  const { clinicalDepartments, departments } = data
  const departmentNames = clinicalDepartmentNames({ departments })
  const downloadClinicalDepartments = () => {
    const sortedDepartments = clinicalDepartments
      .filter(department => department.isDisplay)
      .sort((a, b) => {
        const codeA = parseInt(a.departmentCode, 10)
        const codeB = parseInt(b.departmentCode, 10)
        return codeA - codeB
      })
    const sortedByDepartmentCode = sortedDepartments.map(department => ({
      clinicName: `${ClinicNameRender({ clinicalDepartment: department })}`,
      departmentName: departmentNames[department.name],
      departmentCode: department.departmentCode,
    }))
    downloadCsv(
      sortedByDepartmentCode,
      {
        departmentName: i18n.t('main.departmentName'),
        clinicName: i18n.t('main.clinicName'),
        departmentCode: i18n.t('main.departmentCode'),
      },
      '診療科コード一覧.csv'
    )
  }

  const downloadSample = () => {
    window.location.href = isAvailableShift
      ? `${
          process.env.REACT_APP_ENDPOINT
        }/files/募集シフトサンプル.csv?token=${Auth.getToken()}`
      : `${
          process.env.REACT_APP_ENDPOINT
        }/files/確定シフトサンプル.csv?token=${Auth.getToken()}`
  }

  return (
    <WorkScheduleCsvScene
      popup={popup}
      i18n={i18n}
      downloadClinicalDepartments={downloadClinicalDepartments}
      onSubmit={onSubmit}
      downloadSample={downloadSample}
      isAvailableShift={!!isAvailableShift}
      isUploaded={state.isUploaded}
      checkCsvData={checkCsvData}
      isUploadPermitted={isUploadPermitted}
      isUploadCheckPermitted={isUploadCheckPermitted}
      unpublishedSummary={pendingUpload && pendingUpload.unpublishedSummary}
      onCancelUnpublished={cancelPendingUpload}
      onConfirmUnpublished={confirmPendingUpload}
    />
  )
}

export default WorkScheduleCsv
